import { useEffect, useState } from "react";
import type { ButtonHTMLAttributes } from "react";

import { ScansSummary } from "../components/consultation";
import { QuestionnaireCard } from "../components/questionnaire";
import { Button } from "../components/ui/button";
import { CommentField } from "../components/ui/comment";
import { Icon } from "../components/ui/icons";
import { BackButton, Centered, IconBadge, Screen, SectionLabel, type Tone } from "../components/ui/layout";
import { Spinner } from "../components/ui/spinner";
import { cn } from "../libs/cn";
import { elapsedSeconds, formatDuration, formatMoment, formatMoney, formatTimer, plural } from "../libs/format";
import type { ActivityScans } from "../modules/scans/api";
import { getDocuments, type CandidateDocument } from "../modules/states/api";
import {
  answersBody,
  isGroup,
  OutcomeKind,
  questionnaireComplete,
  questionnaireOf,
  type DraftResult,
  type OutcomeItem,
  type OutcomeNode,
  type Questionnaire,
  type QuestionnaireAnswers,
} from "../modules/states/tree";

import type { Common } from "./screens";

// Запрос клиента, записанный после подбора без результата: ответы анкеты и исход-отказ, к которому они
// подходят (причина со своей анкетой подбора или исход с такой анкетой).
export interface Demand {
  summary: string;
  answers: QuestionnaireAnswers;
  outcome: OutcomeItem | null;
  reasonId: string | null;
}

// Чем начать шаг 2: причина и ответы анкеты, уже известные из подбора.
export interface DetailsPrefill {
  reasonId: string | null;
  answers: QuestionnaireAnswers;
}

const KIND_TONE: Record<OutcomeKind, Tone> = {
  [OutcomeKind.DEAL]: "accent",
  [OutcomeKind.REFUSAL]: "danger",
  [OutcomeKind.NEUTRAL]: "muted",
};

// Результаты консультации копятся: «Ещё результат» добавляет текущий и возвращает к выбору исхода,
// «Завершить» добавляет текущий и отправляет все разом. Так одна встреча может закончиться и
// продажей, и скупкой, и отказом по третьему изделию.
interface DraftProps {
  drafts: DraftResult[];
  onAdd: (result: DraftResult) => void;
  onFinish: (result?: DraftResult) => void;
}

let draftSeq = 0;
const draftKey = () => `r${++draftSeq}`;

// ---------------------------------------------------------------- шаг 1: чем закончилось

function firstDealWithDocument(nodes: OutcomeNode[]): OutcomeItem | null {
  for (const node of nodes) {
    if (!isGroup(node) && node.kind === OutcomeKind.DEAL && node.document) return node;
  }
  return null;
}

export function OutcomesScreen({
  busy,
  error,
  now,
  stateName,
  startedAt,
  outcomes,
  scans,
  demand,
  drafts,
  onRemoveDraft,
  onBack,
  onPick,
  onPickDemand,
  onAdd,
  onFinish,
}: Common &
  DraftProps & {
    stateName: string;
    startedAt: Date;
    outcomes: OutcomeNode[];
    // Клиент и изделия, отсканированные за консультацию, — показываем в «Уже отмечено» без ручного ввода.
    scans: ActivityScans | null;
    // Записанный запрос клиента после подбора без результата.
    demand: Demand | null;
    onRemoveDraft: (key: string) => void;
    onBack: () => void;
    onPick: (outcome: OutcomeItem) => void;
    onPickDemand: (outcome: OutcomeItem, prefill: DetailsPrefill) => void;
  }) {
  // «Похоже, это продажа»: главный исход-сделка верхнего уровня с документом; если 1С считает
  // свежий документ вероятным итогом — предлагаем подтвердить одним тапом.
  const main = firstDealWithDocument(outcomes);
  const mainId = main?.id ?? null;
  const [suggested, setSuggested] = useState<CandidateDocument | null>(null);

  useEffect(() => {
    if (!mainId) return;
    let alive = true;
    getDocuments(mainId)
      .then((documents) => alive && setSuggested(documents.find((document) => document.suggested) ?? null))
      // Подсказка необязательна: не пришла — сотрудник выберет сам.
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [mainId]);

  // Уже отмеченный документ второй раз не предлагаем.
  const suggestion =
    main && suggested && !drafts.some((draft) => draft.body.documentId === suggested.id)
      ? {
          key: draftKey(),
          body: { outcomeId: main.id, documentId: suggested.id },
          label: main.name,
          detail: [suggested.title, formatMoney(suggested.amount)].filter(Boolean).join(" · "),
        }
      : null;

  const hasScans = Boolean(scans?.customer || scans?.items.length);
  const customerName = scans?.customer?.name;
  // Отказ по запросу уже отмечен — второй раз не предлагаем.
  const demandOutcome =
    demand?.outcome && !drafts.some((draft) => draft.body.outcomeId === demand.outcome!.id) ? demand.outcome : null;
  const demandReason = demandOutcome?.refusalReasons.find((reason) => reason.id === demand?.reasonId) ?? null;

  return (
    <Screen
      error={error}
      header={
        <>
          <BackButton onClick={onBack} />
          <span className="text-sm font-semibold text-[var(--color-muted)] tabular-nums">
            {formatTimer(elapsedSeconds(startedAt, now))}
          </span>
        </>
      }
      footer={
        drafts.length > 0 && (
          <Button size="lg" loading={busy} onClick={() => onFinish()}>
            <Icon name="check" />
            Завершить · {drafts.length} {plural(drafts.length, "результат", "результата", "результатов")}
          </Button>
        )
      }
    >
      <h1 className="text-2xl font-semibold text-balance">
        {drafts.length ? "Что ещё было на встрече?" : "Чем закончилось?"}
      </h1>
      <p className="pt-1 pb-3 text-sm text-[var(--color-muted)]">
        {stateName} · {formatDuration(elapsedSeconds(startedAt, now))}
        {customerName && ` · ${customerName}`}
      </p>

      {(drafts.length > 0 || hasScans || demand) && (
        <section className="mb-2 flex flex-col gap-1 rounded-2xl bg-[var(--color-accent-soft)] p-3">
          <h2 className="px-1 text-xs font-bold tracking-wider text-[var(--color-accent)] uppercase">Уже отмечено</h2>
          {scans && hasScans && <ScansSummary scans={scans} />}
          {demand && (
            <div className="flex flex-col rounded-xl bg-[var(--color-surface)] px-3 py-2 leading-tight">
              <span className="font-semibold">Запрос клиента</span>
              <span className="line-clamp-2 text-sm text-[var(--color-muted)]">{demand.summary} — ничего не подошло</span>
            </div>
          )}
          {drafts.map((draft) => (
            <div
              key={draft.key}
              className="flex items-center gap-2 rounded-xl bg-[var(--color-surface)] py-2 pr-1 pl-3"
            >
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="font-semibold">{draft.label}</span>
                {draft.detail && <span className="truncate text-sm text-[var(--color-muted)]">{draft.detail}</span>}
              </span>
              <button
                type="button"
                onClick={() => onRemoveDraft(draft.key)}
                aria-label={`Убрать: ${draft.label}`}
                className="grid size-10 shrink-0 place-items-center rounded-full text-[var(--color-muted)] active:bg-black/5"
              >
                <Icon name="close" className="size-5" />
              </button>
            </div>
          ))}
        </section>
      )}

      {demand && demandOutcome && (
        <div className="mb-2 flex flex-col gap-3 rounded-3xl bg-[var(--color-danger)] p-4 text-white">
          <div className="flex items-start gap-3">
            <Icon name={demandOutcome.icon ?? "diamond"} className="mt-0.5 size-7" />
            <div>
              <p className="text-lg font-semibold">
                {demandOutcome.name}
                {demandReason && `: ${demandReason.name}`}
              </p>
              <p className="text-sm opacity-85">Анкета «что искал» заполнена из подбора — проверьте и подтвердите</p>
            </div>
          </div>
          <Button
            variant="secondary"
            className="text-[var(--color-danger)] ring-0"
            disabled={busy}
            onClick={() => onPickDemand(demandOutcome, { reasonId: demandReason?.id ?? null, answers: demand.answers })}
          >
            Отметить отказ по запросу
          </Button>
        </div>
      )}

      {main && suggestion && (
        <div className="mb-2 flex flex-col gap-3 rounded-3xl bg-[var(--color-accent)] p-4 text-white">
          <div className="flex items-start gap-3">
            <Icon name={main.icon ?? "receipt"} className="mt-0.5 size-7" />
            <div>
              <p className="text-lg font-semibold">Похоже, это {main.name.toLowerCase()}</p>
              <p className="text-sm opacity-85">
                {suggested!.title} · {formatMoment(suggested!.date)}
                {suggested!.amount !== undefined && ` · ${formatMoney(suggested!.amount)}`}
              </p>
            </div>
          </div>
          <Button
            variant="secondary"
            className="text-[var(--color-accent)] ring-0"
            loading={busy}
            onClick={() => onFinish(suggestion)}
          >
            {drafts.length ? "Да, подтвердить и завершить" : "Да, подтвердить"}
          </Button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onAdd(suggestion)}
            className="-mt-1 min-h-10 self-center rounded-xl px-3 text-sm font-semibold text-white/90 active:bg-white/10"
          >
            + и ещё результат
          </button>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {outcomes.map((node) =>
          isGroup(node) ? (
            <div key={node.id} className="flex flex-col gap-2">
              <SectionLabel>{node.name}</SectionLabel>
              {node.children.map((child) => (
                <OutcomeRow
                  key={child.id}
                  outcome={child as OutcomeItem}
                  disabled={busy}
                  onClick={() => onPick(child as OutcomeItem)}
                />
              ))}
            </div>
          ) : (
            <OutcomeRow key={node.id} outcome={node} disabled={busy} onClick={() => onPick(node)} />
          ),
        )}
      </div>
    </Screen>
  );
}

function outcomeHint(outcome: OutcomeItem) {
  if (outcome.hint) return outcome.hint;
  if (outcome.document) return outcome.document.title;
  if (outcome.refusalReasons.length) return "дальше: причина отказа";
  return null;
}

function OutcomeRow({ outcome, ...props }: { outcome: OutcomeItem } & ButtonHTMLAttributes<HTMLButtonElement>) {
  const hint = outcomeHint(outcome);
  return (
    <button
      type="button"
      className="flex min-h-16 w-full items-center gap-3 rounded-2xl bg-[var(--color-surface)] px-3 py-3 text-left ring-1 ring-[var(--color-line)] transition-transform active:scale-[0.99] active:bg-[#ece8de] disabled:opacity-40"
      {...props}
    >
      <IconBadge tone={KIND_TONE[outcome.kind] ?? "muted"} name={outcome.icon ?? null} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="font-semibold">{outcome.name}</span>
        {hint && <span className="text-sm text-[var(--color-muted)]">{hint}</span>}
      </span>
      <Icon name="chevron" className="size-5 text-[var(--color-muted)]" />
    </button>
  );
}

// Низ шагов 2: «Ещё результат» рядом с «Завершить» — оба сначала сохраняют то, что заполнено на экране.
function StepFooter({
  busy,
  ready,
  hint,
  onAdd,
  onFinish,
}: {
  busy: boolean;
  ready: boolean;
  hint: string;
  onAdd: () => void;
  onFinish: () => void;
}) {
  return (
    <>
      {/* Порог в rem: при крупном системном шрифте две кнопки не влезают в ряд и встают в столбик. */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          size="lg"
          className="min-w-[10.5rem] flex-1 px-3 text-base whitespace-nowrap"
          disabled={!ready || busy}
          onClick={onAdd}
        >
          <Icon name="plus" className="size-5" />
          Ещё результат
        </Button>
        <Button
          size="lg"
          className="min-w-[10.5rem] flex-1 px-3 text-base whitespace-nowrap"
          loading={busy}
          disabled={!ready}
          onClick={onFinish}
        >
          <Icon name="check" className="size-5" />
          Завершить
        </Button>
      </div>
      {!ready && <p className="text-center text-xs text-[var(--color-muted)]">{hint}</p>}
    </>
  );
}

// ---------------------------------------------------------------- шаг 2: документ 1С

export function DocumentsScreen({
  busy,
  error,
  outcome,
  drafts,
  onBack,
  onAdd,
  onFinish,
}: Common & DraftProps & { outcome: OutcomeItem; onBack: () => void }) {
  const [documents, setDocuments] = useState<CandidateDocument[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  // Документ, уже отмеченный в этой консультации, второй раз не предлагаем.
  const taken = new Set(drafts.map((draft) => draft.body.documentId).filter(Boolean));

  const load = () => {
    setDocuments(null);
    setLoadFailed(false);
    getDocuments(outcome.id)
      .then((list) => {
        setDocuments(list);
        setSelected((current) => current ?? list.find((document) => document.suggested)?.id ?? null);
      })
      .catch(() => setLoadFailed(true));
  };

  useEffect(load, [outcome.id]);

  const available = documents?.filter((document) => !taken.has(document.id)) ?? null;
  const chosen = available?.find((document) => document.id === selected) ?? null;
  const result = (): DraftResult => ({
    key: draftKey(),
    body: { outcomeId: outcome.id, documentId: chosen!.id, comment: comment.trim() || undefined },
    label: outcome.name,
    detail: [chosen!.title, formatMoney(chosen!.amount)].filter(Boolean).join(" · "),
  });

  return (
    <Screen
      error={error}
      header={<BackButton onClick={onBack} />}
      footer={
        <StepFooter
          busy={busy}
          ready={Boolean(chosen)}
          hint="Выберите документ"
          onAdd={() => onAdd(result())}
          onFinish={() => onFinish(result())}
        />
      }
    >
      <h1 className="text-2xl font-semibold text-balance">{outcome.document?.title ?? "Выберите документ"}</h1>
      <p className="pt-1 pb-3 text-sm text-[var(--color-muted)]">
        {outcome.name} · документы вашего магазина, ещё не привязанные к сотрудникам
      </p>

      {loadFailed ? (
        <Centered>
          <p className="max-w-72 text-center text-[var(--color-muted)]">Не удалось получить документы из 1С.</p>
          <Button variant="secondary" className="w-auto" onClick={load}>
            Повторить
          </Button>
        </Centered>
      ) : available === null ? (
        <Centered>
          <Spinner className="size-8 text-[var(--color-accent)]" />
        </Centered>
      ) : available.length === 0 ? (
        <Centered>
          <IconBadge tone="muted" name={outcome.icon ?? "receipt"} size="lg" />
          <p className="max-w-72 text-center text-[var(--color-muted)]">
            Подходящих документов пока нет. Если документ только что провели — обновите список.
          </p>
          <Button variant="secondary" className="w-auto" onClick={load}>
            <Icon name="refresh" className="size-5" />
            Обновить
          </Button>
        </Centered>
      ) : (
        <div className="flex flex-col gap-2">
          {available.map((document) => (
            <DocumentCard
              key={document.id}
              document={document}
              selected={document.id === selected}
              disabled={busy}
              onClick={() => setSelected(document.id)}
            />
          ))}
          <CommentField value={comment} onChange={setComment} />
        </div>
      )}
    </Screen>
  );
}

function DocumentCard({
  document,
  selected,
  ...props
}: { document: CandidateDocument; selected: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  const [first, second, ...rest] = document.items;
  const items = [first, second].filter(Boolean).map((item) => item.name);
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cn(
        "flex w-full flex-col gap-1 rounded-2xl px-4 py-3 text-left ring-1 transition-transform active:scale-[0.99] disabled:opacity-40",
        selected
          ? "bg-[var(--color-accent-soft)] ring-2 ring-[var(--color-accent)]"
          : "bg-[var(--color-surface)] ring-[var(--color-line)]",
      )}
      {...props}
    >
      <span className="flex items-center justify-between gap-3">
        <span className="font-semibold">{document.title}</span>
        <span className="font-semibold whitespace-nowrap tabular-nums">{formatMoney(document.amount)}</span>
      </span>
      <span className="flex items-center gap-2 text-sm text-[var(--color-muted)]">
        {formatMoment(document.date)}
        {document.suggested && (
          <span className="rounded-full bg-[var(--color-gold-soft)] px-2 py-0.5 text-xs font-semibold text-[var(--color-gold)]">
            похоже, этот
          </span>
        )}
      </span>
      {items.length > 0 && (
        <span className="text-sm leading-snug">
          {items.join(", ")}
          {rest.length > 0 && <span className="text-[var(--color-muted)]"> и ещё {rest.length}</span>}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------- шаг 2: причина, анкеты, комментарий

export function DetailsScreen({
  busy,
  error,
  outcome,
  questionnaires,
  prefill,
  onBack,
  onAdd,
  onFinish,
}: Common &
  DraftProps & {
    outcome: OutcomeItem;
    questionnaires: Questionnaire[];
    // Из подбора: причина и ответы анкеты уже известны.
    prefill?: DetailsPrefill;
    onBack: () => void;
  }) {
  const [reasonId, setReasonId] = useState<string | null>(prefill?.reasonId ?? null);
  const [comment, setComment] = useState("");
  // Анкеты по товарам: покупатель мог искать не одну вещь.
  const [forms, setForms] = useState<QuestionnaireAnswers[]>([prefill?.answers ?? {}]);

  const reasons = outcome.refusalReasons;
  const reason = reasons.find((item) => item.id === reasonId) ?? null;
  const questionnaire = reasons.length && !reason ? null : questionnaireOf(questionnaires, outcome, reasonId);
  const refusal = outcome.kind === OutcomeKind.REFUSAL;
  // У «Другое» причину без пояснения не разобрать — поле комментария открываем сразу.
  const commentOpen = outcome.commentRequired || reason?.name.toLowerCase().startsWith("друг");

  const formsReady = !questionnaire || forms.every((answers) => questionnaireComplete(questionnaire, answers));
  const ready = Boolean((!reasons.length || reason) && (!outcome.commentRequired || comment.trim()) && formsReady);
  const hint =
    reasons.length && !reason
      ? "Выберите причину"
      : !formsReady
        ? "Заполните отмеченные * поля анкеты"
        : "Напишите комментарий";

  const pickReason = (id: string) => {
    // Другая причина может спрашивать другую анкету — начинаем её с чистого листа.
    const next = questionnaireOf(questionnaires, outcome, id);
    if (next?.id !== questionnaire?.id) setForms([{}]);
    setReasonId(id);
  };

  const result = (): DraftResult => {
    const filled = questionnaire ? forms.map((answers) => answersBody(questionnaire, answers)) : [];
    return {
      key: draftKey(),
      body: {
        outcomeId: outcome.id,
        reasonId: reason?.id,
        comment: comment.trim() || undefined,
        questionnaires: questionnaire
          ? filled.map((answers) => ({ questionnaireId: questionnaire.id, answers }))
          : undefined,
      },
      label: reason ? `${outcome.name}: ${reason.name}` : outcome.name,
      detail: questionnaire
        ? `${forms.length} ${plural(forms.length, "анкета", "анкеты", "анкет")} · ${questionnaire.name}`
        : comment.trim() || undefined,
    };
  };

  return (
    <Screen
      error={error}
      header={<BackButton onClick={onBack} />}
      footer={
        <StepFooter
          busy={busy}
          ready={ready}
          hint={hint}
          onAdd={() => onAdd(result())}
          onFinish={() => onFinish(result())}
        />
      }
    >
      <p
        className={cn(
          "text-xs font-bold tracking-wider uppercase",
          refusal ? "text-[var(--color-danger)]" : "text-[var(--color-muted)]",
        )}
      >
        {outcome.name}
      </p>
      <h1 className="pt-1 pb-4 text-2xl font-semibold text-balance">
        {reasons.length ? "Почему клиент ушёл без покупки?" : questionnaire ? "Что спрашивал клиент?" : outcome.name}
      </h1>

      {reasons.length > 0 && (
        <div className="flex flex-wrap gap-2 pb-4">
          {reasons.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={item.id === reasonId}
              disabled={busy}
              onClick={() => pickReason(item.id)}
              className={cn(
                "max-w-full min-h-12 rounded-full px-4 py-2 text-left text-base font-semibold transition-[background-color,transform] active:scale-[0.97]",
                item.id === reasonId
                  ? "bg-[var(--color-ink)] text-white"
                  : "bg-[var(--color-surface)] text-[var(--color-ink)] ring-1 ring-[var(--color-line)]",
              )}
            >
              {item.name}
            </button>
          ))}
        </div>
      )}

      {questionnaire && (
        <div className="flex flex-col gap-2.5 pb-4">
          {forms.map((answers, index) => (
            <QuestionnaireCard
              key={index}
              questionnaire={questionnaire}
              answers={answers}
              index={forms.length > 1 ? index + 1 : null}
              onChange={(next) => setForms(forms.map((item, i) => (i === index ? next : item)))}
              onRemove={forms.length > 1 ? () => setForms(forms.filter((_, i) => i !== index)) : undefined}
            />
          ))}
          <Button
            variant="secondary"
            size="md"
            className="border-2 border-dashed border-[var(--color-line)] bg-transparent ring-0"
            onClick={() => setForms([...forms, {}])}
          >
            <Icon name="plus" className="size-5" />
            Ещё товар
          </Button>
        </div>
      )}

      <CommentField
        value={comment}
        onChange={setComment}
        open={commentOpen}
        placeholder={outcome.commentRequired ? "Комментарий (обязательно)" : undefined}
      />
    </Screen>
  );
}
